/**
 * 📄 LISTE DES FACTURES LOGISTICS
 * ================================
 * Composant pour afficher et gérer les factures d'un dossier d'expédition
 *
 * Fonctionnalités:
 * - Liste toutes les factures d'un shipment spécifique
 * - Filtres: statut paiement, dates, recherche
 * - Actions: Voir détail, Télécharger PDF, Enregistrer paiement, Supprimer
 * - Statistiques rapides (total facturé, payé, en attente)
 * - Auto-refresh après actions
 *
 * Pattern source:
 * - Inspiré de: src/gestion-souscription/components/InvoiceList.tsx
 * - Adapté pour: Context shipment (pas global company)
 *
 * @see src/gestion-logistics/hooks/useInvoices.ts
 * @see src/gestion-logistics/components/InvoiceDetailModal.tsx
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Input,
  Select,
  Button,
  Space,
  Tag,
  message,
  Dropdown,
  Menu,
  Modal,
  DatePicker,
  Row,
  Col,
  Empty,
  theme,
} from 'antd';
import {
  ArrowRightOutlined,
  SearchOutlined,
  DownloadOutlined,
  EyeOutlined,
  DeleteOutlined,
  MoreOutlined,
  DollarOutlined,
  FileTextOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  SwapOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/fr';
import useInvoices from '../hooks/useInvoices';
import { Invoice } from '../types/invoice.types';
import InvoiceDetailModal from './InvoiceDetailModal';
import PaymentRecordModal from './PaymentRecordModal';

import { ShipmentDataTable, ShipmentMetricStrip, ShipmentTabHeading } from "./ShipmentDetailLayout";

const { RangePicker } = DatePicker;
const { Option } = Select;

dayjs.locale('fr');


interface InvoiceListProps {
  shipmentId: number | null;
  onOpenCosting?: () => void;
  onRefreshNeeded?: () => void; // Callback pour refresh Job Costing après modif
}

const InvoiceList: React.FC<InvoiceListProps> = ({ shipmentId, onRefreshNeeded, onOpenCosting }) => {
  const {
    loading,
    invoices,
    fetchInvoicesByShipment,
    downloadInvoicePDF,
    convertProformaToInvoice,
    deleteInvoice,
  } = useInvoices();

  const { token } = theme.useToken();
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Filtres
  const [searchText, setSearchText] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);

  // Statistiques locales (calculées depuis les factures filtrées)
  const [statistics, setStatistics] = useState({
    total_billed: 0,
    total_paid: 0,
    total_outstanding: 0,
  });

  // Fonction utilitaire pour formater les montants
  const formatAmount = (amount: number | null | undefined): string => {
    if (amount === null || amount === undefined || isNaN(amount)) return '0';
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.round(amount));
  };

  const formatCurrency = (amount: number | null | undefined): string => {
    const formatted = formatAmount(amount);
    return `${formatted} XOF`;
  };

  // Charger les factures du dossier
  const loadInvoices = useCallback(async () => {
    if (!shipmentId) {
      return;
    }

    const invoicesList = await fetchInvoicesByShipment(shipmentId);

    // Calculer les statistiques
    const stats = {
      total_billed: invoicesList.reduce((sum: number, inv: Invoice) => sum + (inv.total_amount || 0), 0),
      total_paid: invoicesList.reduce((sum: number, inv: Invoice) => sum + (inv.paid_amount || 0), 0),
      total_outstanding: invoicesList.reduce((sum: number, inv: Invoice) => sum + (inv.balance_due || 0), 0),
    };
    setStatistics(stats);
  }, [shipmentId, fetchInvoicesByShipment]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  // Télécharger le PDF d'une facture
  const handleDownloadPDF = async (invoice: Invoice) => {
    setPdfLoading(true);
    try {
      await downloadInvoicePDF(invoice.id, invoice.invoice_number);
    } finally {
      setPdfLoading(false);
    }
  };

  // Voir détail d'une facture
  const handleViewDetail = (invoice: Invoice) => {
    setSelectedInvoice(invoice);
    setDetailModalVisible(true);
  };

  // Enregistrer un paiement
  const handleRecordPayment = (invoice: Invoice) => {
    if (invoice.invoice_type === 'proforma') {
      message.info('Une proforma ne peut pas recevoir de paiement');
      return;
    }
    if (invoice.payment_status === 'paid') {
      message.info('Cette facture est déjà entièrement payée');
      return;
    }
    setSelectedInvoice(invoice);
    setPaymentModalVisible(true);
  };

  // Supprimer une facture
  const handleDelete = async (invoice: Invoice) => {
    Modal.confirm({
      title: 'Supprimer la facture',
      icon: <ExclamationCircleOutlined aria-hidden="true" />,
      content: `Êtes-vous sûr de vouloir supprimer la facture ${invoice.invoice_number} ?`,
      okText: 'Oui, supprimer',
      okType: 'danger',
      cancelText: 'Annuler',
      onOk: async () => {
        const success = await deleteInvoice(invoice.id);
        if (success) {
          loadInvoices();
          if (onRefreshNeeded) onRefreshNeeded();
        }
      },
    });
  };

  const handleConvertProforma = (invoice: Invoice) => {
    Modal.confirm({
      title: 'Convertir la proforma',
      icon: <SwapOutlined aria-hidden="true" />,
      content: `Convertir la proforma ${invoice.invoice_number} en facture definitive ?`,
      okText: 'Convertir',
      okType: 'primary',
      cancelText: 'Annuler',
      onOk: async () => {
        const result = await convertProformaToInvoice(invoice.id);
        if (result) {
          loadInvoices();
          if (onRefreshNeeded) onRefreshNeeded();
        }
      },
    });
  };

  // Menu d'actions
  const getActionMenu = (invoice: Invoice) => (
    <Menu>
      <Menu.Item
        key="view"
        icon={<EyeOutlined aria-hidden="true" />}
        onClick={() => handleViewDetail(invoice)}
      >
        Voir les détails
      </Menu.Item>

      <Menu.Item
        key="download"
        icon={<DownloadOutlined aria-hidden="true" />}
        onClick={() => handleDownloadPDF(invoice)}
        disabled={pdfLoading}
      >
        {pdfLoading ? 'Génération PDF...' : 'Télécharger PDF'}
      </Menu.Item>

      {invoice.invoice_type === 'proforma' && !invoice.is_converted && (
        <Menu.Item
          key="convert"
          icon={<SwapOutlined aria-hidden="true" />}
          onClick={() => handleConvertProforma(invoice)}
        >
          Convertir en facture definitive
        </Menu.Item>
      )}

      {invoice.invoice_type !== 'proforma' && invoice.payment_status !== 'paid' && (
        <Menu.Item
          key="payment"
          icon={<DollarOutlined aria-hidden="true" />}
          onClick={() => handleRecordPayment(invoice)}
        >
          Enregistrer un paiement
        </Menu.Item>
      )}

      <Menu.Divider />

      <Menu.Item
        key="delete"
        icon={<DeleteOutlined aria-hidden="true" />}
        danger
        onClick={() => handleDelete(invoice)}
        disabled={
          invoice.payment_status === 'paid' ||
          (invoice.invoice_type === 'proforma' && invoice.is_converted)
        }
      >
        Supprimer
      </Menu.Item>
    </Menu>
  );

  // Rendu du statut de paiement
  const renderPaymentStatus = (invoice: Invoice) => {
    if (invoice.invoice_type === 'proforma') {
      return (
        <Space size="small">
          <Tag color="blue">Proforma</Tag>
          {Boolean(invoice.is_converted) && <Tag color="green">Convertie</Tag>}
        </Space>
      );
    }

    const statusConfig: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
      paid: {
        color: 'success',
        icon: <CheckCircleOutlined aria-hidden="true" />,
        label: 'Payée',
      },
      partial: {
        color: 'warning',
        icon: <ClockCircleOutlined aria-hidden="true" />,
        label: 'Partielle',
      },
      unpaid: {
        color: 'default',
        icon: <ClockCircleOutlined aria-hidden="true" />,
        label: 'Non payée',
      },
      overdue: {
        color: 'error',
        icon: <ExclamationCircleOutlined aria-hidden="true" />,
        label: 'En retard',
      },
    };

    const config = statusConfig[invoice.payment_status] || statusConfig.unpaid;

    return (
      <Tag color={config.color} icon={config.icon}>
        {config.label}
      </Tag>
    );
  };

  // Colonnes du tableau
  const columns: ColumnsType<Invoice> = [
    {
      title: 'N° Facture',
      dataIndex: 'invoice_number',
      key: 'invoice_number',
      width: 150,
      render: (text: string, record) => (
        <Space size="small">
          <span className="font-mono font-semibold" style={{ color: "var(--color-primary)" }}>
            {text}
          </span>
          {record.invoice_type === 'proforma' && <Tag color="blue">PROFORMA</Tag>}
        </Space>
      ),
      filteredValue: searchText ? [searchText] : null,
      onFilter: (value, record) =>
        record.invoice_number.toLowerCase().includes((value as string).toLowerCase()) ||
        (record.client_name || '').toLowerCase().includes((value as string).toLowerCase()),
    },
    {
      title: 'Client',
      dataIndex: 'client_name',
      key: 'client_name',
      width: 200,
      render: (text: string, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text || 'N/A'}</div>
          {record.client_email && (
            <div style={{ fontSize: '12px', color: token.colorTextSecondary }}>{record.client_email}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Date',
      key: 'dates',
      width: 140,
      render: (_, record) => (
        <div>
          <div style={{ fontSize: '16px' }}>
            {dayjs(record.invoice_date || record.created_at).format('DD/MM/YYYY')}
          </div>
          {record.due_date && (
            <div style={{ fontSize: '12px', color: token.colorTextSecondary }}>
              Échéance: {dayjs(record.due_date).format('DD/MM/YYYY')}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Montant total', key: 'amount', width: 150, align: 'right' as const,
      render: (_: unknown, record: Invoice) => <strong>{formatCurrency(record.total_amount)}</strong>,

    },
    { title: 'Payé', key: 'paid_amount', width: 140, align: 'right' as const,
      render: (_: unknown, record: Invoice) => <span style={{ color: token.green8 }}>{formatCurrency(record.paid_amount)}</span> },
    { title: 'Solde', key: 'balance_due', width: 140, align: 'right' as const,
      render: (_: unknown, record: Invoice) => <span style={{ color: token.orange8 }}>{formatCurrency(record.balance_due)}</span> },
    {
      title: 'Statut',
      dataIndex: 'payment_status',
      key: 'payment_status',
      width: 120,
      render: (_, record) => renderPaymentStatus(record),
      filteredValue: paymentStatusFilter !== 'all' ? [paymentStatusFilter] : null,
      onFilter: (value, record) => record.payment_status === value,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Dropdown overlay={getActionMenu(record)} trigger={['click']}>
          <Button aria-label={`Actions de la facture ${record.invoice_number}`} type="text" icon={<MoreOutlined aria-hidden="true" />} />
        </Dropdown>
      ),
    },
  ];

  // Filtrer par dates
  const filteredInvoices = (dateRange
    ? invoices.filter((inv) => {
        const invoiceDate = dayjs(inv.invoice_date || inv.created_at);
        return invoiceDate.isAfter(dateRange[0]) && invoiceDate.isBefore(dateRange[1]);
      })
    : invoices).filter(inv => (!searchText || inv.invoice_number.toLowerCase().includes(searchText.toLowerCase()) ||
      (inv.client_name || '').toLowerCase().includes(searchText.toLowerCase())) &&
      (paymentStatusFilter === 'all' || inv.payment_status === paymentStatusFilter));

  if (!shipmentId) {
    return (
      <Card>
        <Empty
          description="Sélectionnez un dossier pour voir ses factures"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    );
  }

  return (
    <div>
      <Card className="shipment-tab-panel">
        <ShipmentTabHeading title="Factures clients" icon={<FileTextOutlined aria-hidden="true" />}
          subtitle="Facturation et règlements du dossier" />
        <ShipmentMetricStrip items={[
          { label: "Total facturé", value: formatCurrency(statistics.total_billed || 0) },
          { label: "Total payé", value: formatCurrency(statistics.total_paid || 0), tone: "positive" },
          { label: "Solde restant", value: formatCurrency(statistics.total_outstanding || 0), tone: "warning" },
        ]} />
        <Card className="shipment-filter-panel">
          <Row gutter={[16, 16]} align="bottom">
            <Col xs={24} md={8}>
              <span className="shipment-filter-label">Recherche</span>
              <Input aria-label="Rechercher une facture client"
                placeholder="N° facture ou client…"
                prefix={<SearchOutlined aria-hidden="true" />} value={searchText} onChange={e => setSearchText(e.target.value)} allowClear />
            </Col>
            <Col xs={24} sm={12} md={5}>
              <span className="shipment-filter-label">Statut de paiement</span>
              <Select aria-label="Statut de paiement" value={paymentStatusFilter} onChange={setPaymentStatusFilter} style={{ width: "100%" }}>
                <Option value="all">Tous les statuts</Option><Option value="unpaid">Non payée</Option>
                <Option value="partial">Partielle</Option><Option value="paid">Payée</Option><Option value="overdue">En retard</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <span className="shipment-filter-label">Période</span>
              <RangePicker aria-label="Période des factures" value={dateRange} onChange={dates => setDateRange(dates as [Dayjs, Dayjs] | null)}
                format="DD/MM/YYYY" placeholder={["Date début", "Date fin"]} style={{ width: "100%" }} />
            </Col>
            <Col xs={24} md={3}><Button icon={<ReloadOutlined aria-hidden="true" />} onClick={loadInvoices} loading={loading} block>Actualiser</Button></Col>
          </Row>
        </Card>
        <div className="shipment-table-panel"><ShipmentDataTable columns={columns} rows={filteredInvoices} loading={loading}
          emptyText="Aucune facture pour ce dossier" /></div>
        <footer className="shipment-tab-footer">
          <p>Les lignes restant à facturer sont accessibles dans Job Costing.</p>
          {onOpenCosting && <Button type="link" onClick={onOpenCosting}>Ouvrir le Job Costing <ArrowRightOutlined aria-hidden="true" /></Button>}

        </footer>
      </Card>

      {/* Modal détail facture */}
      <InvoiceDetailModal
        visible={detailModalVisible}
        invoiceId={selectedInvoice?.id || null}
        onClose={() => {
          setDetailModalVisible(false);
          setSelectedInvoice(null);
        }}
        onRefresh={loadInvoices}
      />

      {/* Modal enregistrement paiement */}
      <PaymentRecordModal
        visible={paymentModalVisible}
        invoice={selectedInvoice}
        onClose={() => {
          setPaymentModalVisible(false);
          setSelectedInvoice(null);
        }}
        onSuccess={() => {
          loadInvoices();
          if (onRefreshNeeded) onRefreshNeeded();
        }}
      />
    </div>
  );
};

export default InvoiceList;
